import type {
  RouteComponent,
  RouteMeta,
  Router,
  RouteRecordRaw,
} from 'vue-router'

/**
 * 快速创建路由。
 * 必须保证 children 是最后一个参数，这样可以让路由编写在代码格式化以后更加便于阅读。
 * 如果不满足你的需求，你可以直接手写一个 RouteRecordRaw 对象。
 * 维护者不要随意修改参数的个数和顺序，否则会影响到路由编写者的使用体验。
 *
 * @alias r
 */
export const newRoute = (
  _path: string | string[] | { path: string | string[], name?: string },
  title: string | RouteMeta,
  _component: RouteComponent | (() => Promise<RouteComponent>) | null,
  children?: Array<RouteRecordRaw | RouteRecordRaw[]>,
): RouteRecordRaw => {
  const getPath = (p: string | string[]) => Array.isArray(p) ? p[0] : p
  const path = (_path instanceof Object && 'path' in _path) ? getPath(_path.path) : getPath(_path)
  const alias = Array.isArray(_path) ? _path.slice(1) : []
  const component = _component && (typeof _component === 'function' ? _component : _component)
  const name = (_path instanceof Object && 'name' in _path) ? _path.name : Math.random().toString()
  const currentRoute: RouteRecordRaw = {
    path: path,
    name,
    meta: typeof title === 'string' ? { title: title || '' } : title,
    component,
    children: children?.flat() ?? [],
    alias,
  }
  return currentRoute
}
export const r = newRoute

export const redirect = (
  from: string,
  to: string,
) => ({
  path: from,
  redirect: (x: { path: string }) => {
    return { path: to.startsWith('/') ? to : x.path.endsWith('/') ? `${x.path}${to}` : `${x.path}/${to}` }
  },
})

type GetQuery = {
  (key: string, fallback: string, router?: Router): string
  (key: string, fallback: number, router?: Router): number
  (key: string, fallback: boolean, router?: Router): boolean
}
/**
 * 获取查询参数，通过默认值推测类型
 * @example
 * ```ts
 * const id = getQuery('id', 0, router)
 * ```
 */
export const getQuery: GetQuery = <T>(key: string, fallback: T, router?: Router) => {
  const getValue = (raw?: string | null) => {
    if (raw === null || raw === undefined) {
      return fallback
    } else if (typeof fallback === 'string') {
      return raw
    } else if (typeof fallback === 'number') {
      return parseFloat(raw)
    } else if (typeof fallback === 'boolean') {
      return (raw !== '' && raw !== '0' && raw !== 'false' && raw !== 'null' && raw !== 'undefined')
    } else {
      return raw
    }
  }
  if (router) {
    const query = router.currentRoute.value.query
    const raw = query[key]
    return getValue(raw?.toString())
  } else {
    const search = window.location.search
    const query = new URLSearchParams(search)
    const raw = query.get(key)
    return getValue(raw)
  }
}
